import { Box } from '@factorialco/deprecated-design-system'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { isEmpty } from 'lodash'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency, { isCurrency } from 'modules/core/lib/money'

import { formatTaxRate } from 'modules/expenses/components/expenses/lib/formatTaxRate'
import { CustomTwoColumnsList } from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/CustomTwoColumnList'
import { Expensable } from 'modules/expenses/types/Expensable'

import CustomWidgetHeaderButton from './CustomWidgetHeaderButton'

interface DocumentInformationWidgetProps {
  expensable: Expensable
  editable: boolean
  onEdit: () => void
}

const calculateAmountLabel = (documentType: string): string => {
  if (documentType === 'invoice') {
    return i18n.t(
      'expenses_v2.confirmation_screen.expense.document_information_block.invoice_document_amount'
    )
  }
  if (documentType === 'receipt') {
    return i18n.t(
      'expenses_v2.confirmation_screen.expense.document_information_block.receipt_document_amount'
    )
  }
  return i18n.t(
    'expenses_v2.confirmation_screen.expense.document_information_block.other_document_amount'
  )
}
const calculateDocumentNumberLabel = (documentType: string): string => {
  if (documentType === 'invoice') {
    return i18n.t(
      'expenses_v2.confirmation_screen.expense.document_information_block.invoice_document_number'
    )
  }
  if (documentType === 'receipt') {
    return i18n.t(
      'expenses_v2.confirmation_screen.expense.document_information_block.receipt_document_number'
    )
  }
  return i18n.t(
    'expenses_v2.confirmation_screen.expense.document_information_block.other_document_number'
  )
}

const buildDocumentInformationListData = (expensable: Expensable, locale: string) => {
  const documentType = expensable?.expense?.documentType ?? ''
  const effectiveOn = expensable?.effectiveOn
    ? moment(expensable.effectiveOn).format('DD/MM/YYYY')
    : ''
  const amount = parseInt(expensable?.amount ?? '0')

  const currency = (expensable?.currency ?? '').toUpperCase()

  const taxes = expensable?.expense?.taxes
    ?.map((tax) => {
      if (!isCurrency(currency)) {
        return ''
      }

      const { percentage, amount: taxAmount } = tax

      if (percentage === null || percentage === undefined) {
        return ''
      }

      if (taxAmount === null || taxAmount === undefined) {
        return ''
      }

      const formattedTaxAmount = MoneyWithCurrency({
        amount: taxAmount,
        currency,
        locale,
      })

      return `${formatTaxRate(percentage)} ${formattedTaxAmount}`
    })
    .join(', ')

  const documentNumber = expensable?.expense?.documentNumber
  const merchantName = expensable?.expense?.merchantName
  const merchantTin = expensable?.expense?.merchantTin

  const amountInfo = isCurrency(currency)
    ? MoneyWithCurrency({
        amount: amount,
        currency,
        locale,
      })
    : ''

  const items = [
    {
      title: i18n.t(
        'expenses_v2.confirmation_screen.expense.document_information_block.issue_date'
      ),
      info: effectiveOn,
    },
    {
      title: calculateAmountLabel(documentType),
      info: amountInfo,
    },
    {
      title: i18n.t('expenses_v2.confirmation_screen.expense.document_information_block.taxes'),
      info: taxes,
    },
    {
      title: calculateDocumentNumberLabel(documentType),
      info: documentNumber,
    },
    {
      title: i18n.t(
        'expenses_v2.confirmation_screen.expense.document_information_block.vendor_name'
      ),
      info: merchantName,
    },
    {
      title: i18n.t(
        'expenses_v2.confirmation_screen.expense.document_information_block.vendor_tin'
      ),
      info: merchantTin,
    },
  ]

  return items.filter(({ info }) => !isEmpty(info))
}

const DocumentInformationWidget = ({
  expensable,
  editable,
  onEdit,
}: DocumentInformationWidgetProps) => {
  const { access } = useCurrentSession()
  const { locale } = access

  const list = buildDocumentInformationListData(expensable, locale)
  if (isEmpty(list)) {
    return null
  }

  return (
    <Box gap="s16" position="relative">
      <Widget
        header={{
          title: i18n.t('expenses_v2.confirmation_screen.expense.document_information_block.title'),
        }}
      >
        <CustomTwoColumnsList list={list} />
      </Widget>
      {editable && (
        <CustomWidgetHeaderButton
          label={i18n.t('expenses_v2.confirmation_screen.confirmation_screen_block_edit_action')}
          onClick={onEdit}
        />
      )}
    </Box>
  )
}

export default DocumentInformationWidget
